
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { InputWithEmojiPicker } from '@/components/ui/InputWithEmojiPicker';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { X } from 'lucide-react';
import { Task } from '@/context/TaskContext';
import { toast } from 'sonner';

interface TaskFormProps {
  onSave: (title: string, detail: string) => void;
  onCancel: () => void;
  taskToEdit: Task | null;
  isEditing: boolean;
}

const TaskForm: React.FC<TaskFormProps> = ({
  onSave,
  onCancel,
  taskToEdit,
  isEditing
}) => {
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  
  // Character limit for task detail
  const characterLimit = 200;
  const characterCount = detail.length;

  // Set form data when editing a task
  useEffect(() => {
    if (taskToEdit) {
      setTitle(taskToEdit.title);
      setDetail(taskToEdit.detail || taskToEdit.description || '');
    }
  }, [taskToEdit]);

  const handleSave = () => {
    if (title.trim()) {
      onSave(title, detail);
      setTitle('');
      setDetail('');
    }
  };

  return (
    <form 
      className="flex flex-col space-y-6 w-full max-w-xl"
      onSubmit={e => { e.preventDefault(); handleSave(); }}
    >
      <div className="space-y-2">
        <Label htmlFor="task-title" className="text-sm font-medium">
          Task Title
        </Label>
        <InputWithEmojiPicker
          id="task-title"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="What are you working on?"
          className="w-full text-lg py-3 px-4"
          autoFocus
          maxLength={120}
          required
        />
      </div>
      
      <div className="space-y-2">
        <div className="flex justify-between">
          <Label htmlFor="task-detail" className="text-sm font-medium">
            Detail
          </Label>
          <span className={`text-xs ${characterCount > characterLimit ? 'text-red-600' : 'text-muted-foreground'}`}>
            {characterCount}/{characterLimit}
          </span>
        </div>
        <Textarea
          id="task-detail"
          value={detail}
          onChange={e => {
            if (e.target.value.length <= characterLimit) {
              setDetail(e.target.value);
            }
          }}
          placeholder="Add some details about your task..."
          rows={4}
          className="w-full min-h-[100px] text-base px-4 py-3"
          maxLength={characterLimit}
        />
      </div>
      
      <div className="flex justify-end gap-3">
        <Button 
          type="button"
          variant="outline"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          disabled={!title.trim()}
          variant="default"
          type="submit"
        >
          {isEditing ? 'Update Task' : 'Save Task'}
        </Button>
      </div>
    </form>
  );
};

export default TaskForm;
